import { extname, resolve } from 'node:path'
import { ast, type ExportNode, type FileNode, type SourceNode } from '@kubb/ast'
import { Diagnostics } from '@kubb/core'
import type { Config, NormalizedPlugin } from '@kubb/core'
import { toPosixPath } from '@internals/utils'
import type { BarrelType } from './types.ts'

const SOURCE_EXTENSIONS = new Set(['.ts', '.tsx', '.js', '.jsx'])
const BARREL_SUFFIX = `/index.ts`

/**
 * A node in the directory tree used to compute barrel file exports.
 * Either represents a directory (with `children`) or a file (`isFile: true`, empty `children`).
 */
type BuildTree = {
  /**
   * Absolute filesystem path of this directory or file. Always normalized to POSIX (`/`) separators.
   */
  path: string
  /**
   * Sub-directories and files contained within this directory.
   * Always empty for file nodes.
   */
  children: Array<BuildTree>
  /**
   * `true` when this node represents a file (leaf), `false` for directory nodes.
   */
  isFile: boolean
}

/**
 * Builds a directory tree rooted at `rootPath` from a list of absolute file paths.
 * Paths outside `rootPath` are silently ignored. Children are sorted alphabetically
 * by path so consumers (barrel exports, propagated indexes) emit a deterministic order.
 *
 * Both POSIX (`/`) and Windows (`\`) separators are accepted in input paths; emitted node
 * paths are always POSIX-normalized so downstream prefix/lookup operations behave the same
 * across platforms.
 *
 * @example
 * ```ts
 * buildTree('/src/gen/types', [
 *   '/src/gen/types/pet.ts',
 *   '/src/gen/types/pets/listPets.ts',
 * ])
 * ```
 */
export function buildTree(rootPath: string, filePaths: ReadonlyArray<string>): BuildTree {
  const normalizedRoot = toPosixPath(rootPath)
  const root: BuildTree = { path: normalizedRoot, children: [], isFile: false }
  // Per-directory child lookup avoids the O(N) `Array.find` scan during insertion.
  // WeakMap keyed by object identity so directory nodes are GC-eligible once the tree is discarded.
  const childIndex = new WeakMap<BuildTree, Map<string, BuildTree>>()
  childIndex.set(root, new Map())

  const rootPrefix = `${normalizedRoot}/`

  for (const filePath of filePaths) {
    const normalized = toPosixPath(filePath)
    if (!normalized.startsWith(rootPrefix)) continue

    const parts = normalized.slice(rootPrefix.length).split('/')
    if (parts.length === 0) continue

    let current = root
    const lastIndex = parts.length - 1
    for (const [i, part] of parts.entries()) {
      if (!part) continue

      const isLast = i === lastIndex
      const siblings = childIndex.get(current)!
      let child = siblings.get(part)
      if (!child) {
        child = { path: `${current.path}/${part}`, children: [], isFile: isLast }
        current.children.push(child)
        siblings.set(part, child)
        if (!isLast) childIndex.set(child, new Map())
      }
      current = child
    }
  }

  sortTree(root)

  return root
}

function sortTree(node: BuildTree): void {
  if (node.children.length === 0) return
  node.children.sort(compareByPath)

  for (const child of node.children) {
    if (!child.isFile) sortTree(child)
  }
}

function compareByPath(a: BuildTree, b: BuildTree): number {
  return a.path < b.path ? -1 : a.path > b.path ? 1 : 0
}

function toRelativeModulePath(fromDir: string, filePath: string): string {
  return `./${filePath.slice(fromDir.length + 1)}`
}

function isBarrelPath(path: string): boolean {
  return path.endsWith(BARREL_SUFFIX)
}

function makeBarrel(dirPath: string, exports: Array<ExportNode>, sourceFiles: ReadonlyMap<string, FileNode>, reportedCollisions: Set<string>): FileNode {
  const names = new Map<string, string>()
  const uniqueExports: Array<ExportNode> = []

  for (const item of exports) {
    const itemPath = toPosixPath(resolve(dirPath, item.path))
    const itemNames = Array.isArray(item.name)
      ? item.name
      : item.name
        ? [item.name]
        : sourceFiles.get(itemPath)?.sources.flatMap((source) => (source.name ? [source.name] : []))
    const uniqueNames: Array<string> = []

    for (const name of itemNames ?? []) {
      const first = names.get(name)
      if (!first) {
        names.set(name, item.path)
        uniqueNames.push(name)
        continue
      }

      const key = [name, toPosixPath(resolve(dirPath, first)), itemPath].join('\0')
      if (reportedCollisions.has(key)) continue
      reportedCollisions.add(key)
      Diagnostics.report({
        code: Diagnostics.code.barrelDuplicateExport,
        severity: 'error',
        message: `"${name}" is exported by both "${first}" and "${item.path}", so "${dirPath}${BARREL_SUFFIX}" cannot re-export both.`,
        help: 'Rename one of the colliding declarations, or configure the plugin resolver to produce distinct names.',
      })
    }

    if (!itemNames || item.name == null || uniqueNames.length > 0) {
      uniqueExports.push(item.name == null ? item : ast.factory.createExport({ ...item, name: Array.isArray(item.name) ? uniqueNames : uniqueNames[0] }))
    }
  }

  return ast.factory.createFile({
    baseName: 'index.ts',
    path: `${dirPath}${BARREL_SUFFIX}`,
    exports: uniqueExports,
    sources: [],
    imports: [],
    // Default to no banner/footer. The barrel plugin resolves a configured plugin
    // banner/footer (with isBarrel: true) afterwards, so a `banner` function can
    // decide per file whether a barrel should carry a directive like "use server".
    banner: undefined,
    footer: undefined,
  })
}

type LeafContext = {
  dirPath: string
  leafPath: string
  sourceFile: FileNode | null
}

type LeafStrategy = (ctx: LeafContext) => Array<ExportNode>

function hasOnlyNonIndexableSources(sources: ReadonlyArray<SourceNode>): boolean {
  if (sources.length === 0) return false
  for (const source of sources) {
    if (source.isIndexable) return false
  }
  return true
}

function partitionIndexableNames(sources: ReadonlyArray<SourceNode>): Map<boolean, Set<string>> {
  const byTypeOnly = new Map<boolean, Set<string>>([
    [false, new Set()],
    [true, new Set()],
  ])
  for (const source of sources) {
    if (!source.isIndexable || !source.name) continue
    byTypeOnly.get(Boolean(source.isTypeOnly))!.add(source.name)
  }
  return byTypeOnly
}

const allStrategy: LeafStrategy = ({ dirPath, leafPath, sourceFile }) => {
  if (sourceFile && hasOnlyNonIndexableSources(sourceFile.sources)) return []
  return [ast.factory.createExport({ path: toRelativeModulePath(dirPath, leafPath) })]
}

const namedStrategy: LeafStrategy = ({ dirPath, leafPath, sourceFile }) => {
  const modulePath = toRelativeModulePath(dirPath, leafPath)

  if (!sourceFile) return [ast.factory.createExport({ path: modulePath })]

  const namesByTypeOnly = partitionIndexableNames(sourceFile.sources)
  const valueNames = namesByTypeOnly.get(false)!
  const typeNames = namesByTypeOnly.get(true)!

  if (valueNames.size === 0 && typeNames.size === 0) {
    if (sourceFile.sources.length > 0) return []
    return [ast.factory.createExport({ path: modulePath })]
  }

  const exports: Array<ExportNode> = []
  if (valueNames.size > 0) {
    exports.push(ast.factory.createExport({ name: [...valueNames].sort(), path: modulePath }))
  }
  if (typeNames.size > 0) {
    exports.push(ast.factory.createExport({ name: [...typeNames].sort(), path: modulePath, isTypeOnly: true }))
  }
  return exports
}

const LEAF_STRATEGIES: ReadonlyMap<BarrelType, LeafStrategy> = new Map([
  ['all', allStrategy],
  ['named', namedStrategy],
])

type LeafWalkParams = {
  sourceFiles: ReadonlyMap<string, FileNode>
  strategy: LeafStrategy
  recursive: boolean
  reportedCollisions: Set<string>
}

/**
 * Post-order walk that yields a barrel per visited directory.
 * Returns the list of leaf file paths collected in this subtree (used by the parent call).
 */
function* walkAllOrNamed(node: BuildTree, params: LeafWalkParams, isRoot: boolean): Generator<FileNode, Array<string>> {
  const subtreeLeaves: Array<string> = []

  for (const child of node.children) {
    if (child.isFile) {
      if (!isBarrelPath(child.path)) subtreeLeaves.push(child.path)
      continue
    }

    const childLeaves = yield* walkAllOrNamed(child, params, false)
    for (const leaf of childLeaves) subtreeLeaves.push(leaf)
  }

  if (!isRoot && !params.recursive) return subtreeLeaves

  const exports = subtreeLeaves.flatMap((leafPath) => params.strategy({ dirPath: node.path, leafPath, sourceFile: params.sourceFiles.get(leafPath) ?? null }))

  if (exports.length > 0) {
    yield makeBarrel(node.path, exports, params.sourceFiles, params.reportedCollisions)
  }

  return subtreeLeaves
}

type NestedWalkParams = {
  sourceFiles: ReadonlyMap<string, FileNode>
  strategy: LeafStrategy
  reportedCollisions: Set<string>
}

/**
 * Recursive walk that yields one barrel per directory, re-exporting files and sub-barrels.
 * Used when nested: true. Leaf files honor the barrel `strategy`, so `named` emits explicit
 * named exports instead of wildcards. Sub-directory barrels are chained with a wildcard
 * re-export, which forwards the names the child barrel already curated. Returns whether this
 * node yielded a barrel, so a parent never re-exports a sub-directory that produced nothing.
 */
function* walkNested(node: BuildTree, params: NestedWalkParams): Generator<FileNode, boolean> {
  const exports: Array<ExportNode> = []

  for (const child of node.children) {
    if (child.isFile) {
      if (isBarrelPath(child.path)) continue
      const sourceFile = params.sourceFiles.get(child.path) ?? null
      exports.push(...params.strategy({ dirPath: node.path, leafPath: child.path, sourceFile }))
      continue
    }

    const childYieldedBarrel = yield* walkNested(child, params)
    if (childYieldedBarrel) {
      exports.push(ast.factory.createExport({ path: toRelativeModulePath(node.path, `${child.path}${BARREL_SUFFIX}`) }))
    }
  }

  if (exports.length > 0) {
    yield makeBarrel(node.path, exports, params.sourceFiles, params.reportedCollisions)
    return true
  }

  return false
}

type IndexedFiles = {
  sourceFiles: ReadonlyMap<string, FileNode>
  paths: ReadonlyArray<string>
}

function indexRelevantFiles(files: ReadonlyArray<FileNode>, outputPath: string): IndexedFiles {
  const outputPrefix = `${toPosixPath(outputPath)}/`
  const sourceFiles = new Map<string, FileNode>()
  const paths: Array<string> = []

  for (const file of files) {
    const normalized = toPosixPath(file.path)
    if (!normalized.startsWith(outputPrefix)) continue
    if (isBarrelPath(normalized)) continue
    if (!SOURCE_EXTENSIONS.has(extname(normalized))) continue

    sourceFiles.set(normalized, file)
    paths.push(normalized)
  }

  return { sourceFiles, paths }
}

/**
 * A directory tree plus the source-file lookup it was built from, scoped to a single output root.
 * Build it once with {@link buildBarrelIndex} and derive every barrel (per-plugin and root) from
 * it via {@link getBarrelFiles}, instead of re-scanning the full file set once per barrel.
 */
type BarrelIndex = {
  tree: BuildTree
  sourceFiles: ReadonlyMap<string, FileNode>
}

/**
 * Indexes `files` once for the directory rooted at `outputPath`: filters to indexable source
 * files under that path and builds their directory tree. Reuse the result across every barrel
 * derived from the same root rather than re-filtering and re-building per barrel.
 */
export function buildBarrelIndex(outputPath: string, files: ReadonlyArray<FileNode>): BarrelIndex {
  const { sourceFiles, paths } = indexRelevantFiles(files, outputPath)
  return { tree: buildTree(outputPath, paths), sourceFiles }
}

/**
 * Locates the node for `targetPath` within an index tree, walking down through directory nodes
 * only. Returns `undefined` when no file exists at or under `targetPath` (nothing to barrel).
 */
function findNode(node: BuildTree, targetPath: string): BuildTree | undefined {
  if (node.path === targetPath) return node
  if (node.isFile || !targetPath.startsWith(`${node.path}/`)) return undefined

  for (const child of node.children) {
    if (!child.isFile && (child.path === targetPath || targetPath.startsWith(`${child.path}/`))) {
      return findNode(child, targetPath)
    }
  }

  return undefined
}

type GetBarrelFilesParams = {
  /**
   * Index built once via {@link buildBarrelIndex} for the shared output root.
   */
  index: BarrelIndex
  /**
   * Absolute directory the barrel(s) should be rooted at, a subtree of the index root.
   * Defaults to the index root.
   */
  targetPath?: string
  /**
   * Export strategy used when emitting each barrel.
   * - `'all'` re-exports the whole module (`export * from './x'`)
   * - `'named'` re-exports only the indexable named symbols
   */
  barrelType: BarrelType
  /**
   * Generate an `index.ts` in every sub-directory, each re-exporting only what's directly inside it (hierarchical).
   * When false, uses flat generation strategy with optional recursive subdirectory barrels.
   */
  nested?: boolean
  /**
   * Also generate a barrel for each sub-directory when nested is false.
   * No effect when nested is true (always generates hierarchical structure).
   */
  recursive?: boolean
  /**
   * Collision identities reported by related barrel generations in the same build.
   */
  reportedCollisions?: Set<string>
}

/**
 * Yields barrel `FileNode`s for `targetPath` (or the index root), derived from a shared index.
 * Locating the subtree is a bounded walk down from the root, so deriving many barrels (one per
 * plugin, plus the root) from one index avoids re-scanning the full file set for each.
 *
 * @example
 * ```ts
 * const index = buildBarrelIndex(outputPath, files)
 * for (const file of getBarrelFiles({ index, targetPath, barrelType })) {
 *   upsertFile(file)
 * }
 * ```
 */
export function* getBarrelFiles({
  index,
  targetPath,
  barrelType,
  nested = false,
  recursive = false,
  reportedCollisions = new Set(),
}: GetBarrelFilesParams): Generator<FileNode> {
  const node = targetPath ? findNode(index.tree, toPosixPath(targetPath)) : index.tree
  if (!node) return

  const strategy = LEAF_STRATEGIES.get(barrelType)
  if (!strategy) return

  if (nested) {
    yield* walkNested(node, { sourceFiles: index.sourceFiles, strategy, reportedCollisions })
    return
  }

  yield* walkAllOrNamed(node, { sourceFiles: index.sourceFiles, strategy, recursive, reportedCollisions }, true)
}

/**
 * Builds a POSIX-normalized prefix for a plugin's output. A directory output gets a trailing `/`,
 * while a `mode: 'file'` output (the path is the file itself) gets the exact path with no trailing `/`.
 *
 * Used to detect (and later exclude) files generated by plugins that opted out of the root barrel.
 */
export function getPluginOutputPrefix(plugin: NormalizedPlugin, config: Config): string {
  const resolved = toPosixPath(resolve(config.root, config.output.path, plugin.options.output.path))
  return plugin.options.output.mode === 'file' ? resolved : `${resolved}/`
}

/**
 * Returns `true` when `filePath` lives under any of the given excluded prefixes. A prefix with a
 * trailing `/` matches a directory subtree, and a prefix without one matches that exact file
 * (used for `mode: 'file'` outputs).
 *
 * Both sides are POSIX-normalized so Windows backslash paths match correctly.
 */
export function isExcludedPath(filePath: string, prefixes: ReadonlySet<string>): boolean {
  const normalized = toPosixPath(filePath)
  // Plain `for...of` over the Set rather than `.values().some()`: the iterator-helper `some`
  // allocates an iterator object per call, and this runs once per file during barrel generation.
  for (const prefix of prefixes) {
    const matched = prefix.endsWith('/') ? normalized.startsWith(prefix) : normalized === prefix
    if (matched) return true
  }
  return false
}
